import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { EditorHero, MonoLabel, EditorialButton, SectionCap } from '../components/Editorial';
import Button from '../components/Button';
import BillingAthletePicker from '../components/BillingAthletePicker';
import { useBillingTarget, billingBody, parseAthleteIdParam } from '../lib/billingTarget';

const BILLING_URL = `${window.location.origin}/billing`;

// Pro copy. Every line here describes what the software DOES — the honesty
// gate in ios/ProZpectsTests/PaywallCopyGuardTests.swift bars outcome
// promises until real coach engagement exists. "Fit" is a property of the
// data on both sides; "schools that want you" would be a claim about a third
// party's intent we cannot observe.
const PLAN_FEATURES = [
  'See which colleges fit you, and why — ranked across every signal on your profile',
  'Coach email, phone, and social handles for every program you\'re looking at',
  'Unlimited video',
];

// Free is not a teaser — say what it is, plainly.
const FREE_FEATURES = [
  'Your profile is live in coach search — verified, shareable, and free, permanently',
  'Reply to any college coach who contacts you',
  'Verified measurables with the blue check',
  'Your ProZpects Power Score',
  'Build and share your athlete trading card',
];

export default function UpgradePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [subscription, setSubscription] = useState(null);

  // Whose plan this page sells: the athlete's — for a guardian, the athlete
  // they manage (see lib/billingTarget.js), never the guardian's own row.
  // ?athlete_id= (from the parent-welcome link) is honored only if it is one
  // of this guardian's linked athletes — the helper checks.
  const [searchParams] = useSearchParams();
  const target = useBillingTarget({
    requestedAthleteId: parseAthleteIdParam(searchParams.get('athlete_id')),
  });
  const targetId = target.athleteUserId;

  // If the target athlete already has an active sub, bounce straight to the dashboard.
  useEffect(() => {
    let cancelled = false;
    setSubscription(null);
    const check = async () => {
      try {
        const r = await axios.get(`${BILLING_URL}/status/${targetId}`);
        if (cancelled) return;
        setSubscription(r.data);
        if (r.data?.status === 'active' || r.data?.status === 'trialing') {
          navigate('/dashboard', { replace: true });
        }
      } catch {
        // ignore — likely "not configured" or network blip; show the page anyway.
      }
    };
    if (targetId != null) check();
    return () => { cancelled = true; };
  }, [targetId, navigate]);

  const startCheckout = async (priceKey) => {
    if (targetId == null) return;  // guardian hasn't picked an athlete yet
    setError('');
    setLoading(true);
    try {
      const r = await axios.post(
        `${BILLING_URL}/checkout/create-session`,
        billingBody(user, targetId, { price_key: priceKey }),
      );
      const url = r.data?.checkout_url || r.data?.url;
      if (!url) throw new Error('Stripe did not return a checkout URL');
      window.location.href = url;
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Could not start checkout');
      setLoading(false);
    }
  };

  // Show "Maybe later" link only if they came from somewhere internal (login,
  // register). If they hit /upgrade directly, hide it — we want commitment.
  const cameFromInternal = location.state?.from || false;

  return (
    <div className="ed-page">
      <EditorHero
        eyebrow={
          <>
            <MonoLabel onDark>UPGRADE</MonoLabel>
            <MonoLabel onDark>·</MonoLabel>
            <MonoLabel onDark>ATHLETE</MonoLabel>
          </>
        }
        headline="Get on the"
        accent="recruiting board."
        sub="Your profile is already live in coach search. Pro gives you the contact information and the place to keep your recruiting organized."
      />

      <SectionCap label="What you already have" hint="FREE, PERMANENTLY" />
      <div className="ed-card" style={{ marginBottom: 24 }}>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {FREE_FEATURES.map((f) => (
            <li key={f} style={{ marginBottom: 8 }}>{f}</li>
          ))}
        </ul>
      </div>

      <SectionCap label="What you get" hint="WITH PRO" />

      <div className="ed-card" style={{ marginBottom: 24 }}>
        <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.9 }}>
          {PLAN_FEATURES.map((f) => (
            <li key={f}>{f}</li>
          ))}
        </ul>
      </div>

      <SectionCap label="Choose your plan" hint="CANCEL ANYTIME" />

      {error && (
        <div className="ed-card" style={{ borderColor: '#dc2626', color: '#dc2626', marginBottom: 16 }}>
          {error}
        </div>
      )}

      <BillingAthletePicker target={target} />

      <div className="ed-grid ed-grid--2">
        <div className="ed-card" style={{ display: 'flex', flexDirection: 'column' }}>
          <MonoLabel>MONTHLY</MonoLabel>
          <div style={{ fontSize: 48, fontWeight: 700, color: '#0f172a', marginTop: 8 }}>
            $19.99<span style={{ fontSize: 18, fontWeight: 400, color: '#64748b' }}>/month</span>
          </div>
          <p style={{ color: '#64748b', marginBottom: 16 }}>
            Month-to-month. Pause or cancel anytime from your billing portal.
          </p>
          <div style={{ marginTop: 'auto' }}>
            <EditorialButton
              variant="solid"
              onClick={() => startCheckout('monthly')}
              disabled={loading || targetId == null}
              style={{ width: '100%' }}
            >
              {loading ? 'Loading…' : 'Subscribe'}
            </EditorialButton>
          </div>
        </div>

        <div className="ed-card" style={{ display: 'flex', flexDirection: 'column', borderColor: '#0066CC' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <MonoLabel>ANNUAL</MonoLabel>
            <span style={{ background: '#0066CC', color: 'white', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>
              SAVE 17%
            </span>
          </div>
          <div style={{ fontSize: 48, fontWeight: 700, color: '#0f172a', marginTop: 8 }}>
            $199.99<span style={{ fontSize: 18, fontWeight: 400, color: '#64748b' }}>/year</span>
          </div>
          <p style={{ color: '#64748b', marginBottom: 16 }}>
            Best value. ~$16.66/month. Recruiting cycles are a year — match your investment.
          </p>
          <div style={{ marginTop: 'auto' }}>
            <EditorialButton
              variant="solid"
              onClick={() => startCheckout('annual')}
              disabled={loading || targetId == null}
              style={{ width: '100%' }}
            >
              {loading ? 'Loading…' : 'Subscribe'}
            </EditorialButton>
          </div>
        </div>
      </div>

      {subscription?.status === 'canceled' && (
        <p style={{ marginTop: 24, color: '#64748b', textAlign: 'center', fontSize: 13 }}>
          Your previous subscription is canceled. Re-subscribing starts a new billing period.
        </p>
      )}

      <div style={{ marginTop: 32, textAlign: 'center' }}>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate('/dashboard')}
        >
          Maybe later — go to my dashboard
        </Button>
      </div>
    </div>
  );
}
